<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.print-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Printer setup') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app" to="/admin/settings">{{ $t('Back') }}</router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <form @submit.prevent="save">
        <div class="content-card-body">
          <loading :status="loading"></loading>
          <div class="px-4 py-5 sm:p-6">
            <strong class="form-description-head">{{ $t('Invoice printing setup') }}</strong>
            <div class="grid grid-cols-3 gap-6 mt-5">
              <div class="col-span-3 md:flex md:space-x-2">
                <div class="w-full">
                  <label class="form-label" for="print_print_font_family">{{ $t('Font family') }}</label>
                  <div class="pt-1">
                    <select class="form-input" id="print_font_family" required v-model="printer.print_font_family">
                      <option :value="null" disabled>{{ $t('select a option') }}</option>
                      <option value="auto">{{ $t('Auto') }}</option>
                      <option value="monospace">{{ $t('Monospace') }}</option>
                      <option value="sans-serif">{{ $t('Sans-serif') }}</option>
                      <option value="inherit">{{ $t('Inherit') }}</option>
                    </select>
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="print_font_color">{{ $t('Font color') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Font color')" class="form-input" id="print_font_color" required v-model="printer.print_font_color" />
                  </div>
                </div>
              </div>
              <div class="col-span-3 md:flex md:space-x-2">
                <div class="w-full">
                  <label class="form-label" for="print_font_size">{{ $t('Font size') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Font size')" type="number" min="10" max="50" class="form-input" id="print_font_size" required v-model="printer.print_font_size" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="print_qr_code_size">{{ $t('QR code size') }}</label>
                  <div class="pt-1">
                    <input
                      :placeholder="$t('QR code size')"
                      type="number"
                      max="120"
                      min="20"
                      class="form-input"
                      id="print_qr_code_size"
                      required
                      v-model="printer.print_qr_code_size"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="col-span-3 md:flex md:space-x-2">
                 <div class="w-full">
                  <label class="form-label" for="print_printer_width">{{ $t('Invoice paper width') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Invoice paper width')" class="form-input" type="text" id="print_printer_width" required v-model="printer.print_printer_width" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="print_printer_height">{{ $t('Invoice paper height') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Invoice paper height')" class="form-input" type="text" id="print_printer_height" required v-model="printer.print_printer_height" />
                  </div>
                </div>

              </div>
            </div>
          </div>
          <div class="px-4 py-5 sm:p-6">
            <strong class="form-description-head">{{ $t('Label printing setup') }}</strong>
            <div class="grid grid-cols-3 gap-6 mt-5">
              <div class="col-span-3 md:flex md:space-x-2">
                <div class="w-full">
                  <label class="form-label" for="label_text_font_size">{{ $t('Label text font size') }}</label>
                  <div class="pt-1">
                    <input
                      :placeholder="$t('Label text font size')"
                      type="number"
                      max="100"
                      min="5"
                      class="form-input"
                      id="label_text_font_size"
                      required
                      v-model="printer.label_text_font_size"
                      required
                    />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_cols">{{ $t('Label cols in a row') }}</label>
                  <div class="pt-1">
                    <select class="form-input" id="label_cols" required v-model="printer.label_cols">
                      <option :value="1">1</option>
                      <option :value="2">2</option>
                      <option :value="3">3</option>
                      <option :value="4">4</option>
                      <option :value="5">5</option>
                      <option :value="6">6</option>
                      <option :value="7">7</option>
                      <option :value="8">8</option>
                      <option :value="9">9</option>
                    </select>
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_barcode_symbology">{{ $t('Label barcode symbology') }}</label>
                  <div class="pt-1">
                    <select class="form-input" id="label_barcode_symbology" required v-model="printer.label_barcode_symbology">
                      <option :value="null" disabled>
                        {{ $t('Select an option') }}
                      </option>
                      <option value="CODE128">CODE128</option>
                      <option value="CODE39">CODE39</option>
                      <option value="EAN13">EAN13</option>
                      <option value="MSI">MSI</option>
                    </select>
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_copies">{{ $t('Label default copies') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Label default copies')" type="number" min="1" max="50" class="form-input" id="label_copies" required v-model="printer.label_copies" />
                  </div>
                </div>
              </div>
              <div class="col-span-3 md:flex md:space-x-2">
                <div class="w-full">
                  <label class="form-label" for="product_barcode_length">{{ $t('Product label lenth') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Product barcode length')" min="5" max="15" type="number" class="form-input" id="product_barcode_length" required v-model="printer.product_barcode_length" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_width">{{ $t('Label width') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Label width')" class="form-input" id="label_width" required v-model="printer.label_width" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_height">{{ $t('Label height') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Label height')" class="form-input" type="text" id="label_height" required v-model="printer.label_height" />
                  </div>
                </div>
              </div>
              <div class="col-span-3 md:flex md:space-x-2">
                <div class="w-full">
                  <label class="form-label" for="label_paper_width">{{ $t('Label paper width') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Label paper width')" class="form-input" type="text" id="label_paper_width" required v-model="printer.label_paper_width" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="form-label" for="label_paper_height">{{ $t('Label paper height') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Label paper height')" class="form-input" type="text" id="label_paper_height" required v-model="printer.label_paper_height" />
                  </div>
                </div>
              </div>
              <div class="col-span-3 md:flex md:justify-between">
              <div class="w-full">
                <label class="form-label" for="label_around_border">
                  {{ $t('Label border') }}
                </label>
                <input-switch :disabled-label="$t('Disabled')" :enabled-label="$t('Enabled')" id="label_around_border" v-model="printer.label_around_border"></input-switch>
              </div>
               <div class="w-full">
                <label class="form-label" for="label_display_app_name">
                  {{ $t('App name on label') }}
                </label>
                <input-switch :disabled-label="$t('Disabled')" :enabled-label="$t('Enabled')" id="label_display_app_name" v-model="printer.label_display_app_name"></input-switch>
              </div>
               <div class="w-full">
                <label class="form-label" for="label_swich_to_imei">
                  {{ $t('Label based on IMEI') }}
                </label>
                <input-switch :disabled-label="$t('Disabled')" :enabled-label="$t('Enabled')" id="label_swich_to_imei" v-model="printer.label_swich_to_imei"></input-switch>
              </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <div class="flex justify-between">
              <p class="form-description-text text-left">
                {{ $t('Attention') }}:
                {{ $t('Please dont foget to reload after setting up print configurations.') }}
              </p>
              <button class="btn btn-app" type="submit">
                {{ $t('Save') }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>
<script>
export default {
  name: 'printer',
  metaInfo() {
    return {
      title: this.$i18n.t('Printer setup'),
    };
  },
  data() {
    return {
      loading: true,
      printer: {
        print_font_color: 'black',
        print_font_family: 'monospace',
        print_font_size: 11,
        print_printer_height: '297mm',
        print_printer_width: '80mm',
        print_qr_code_size: 70,
        label_cols: 4,
        label_barcode_symbology: 'CODE128',
        label_copies: 11,
        label_width: 1,
        label_height: 35,
        label_text_font_size: 11,
        label_paper_width: '50mm',
        label_paper_height: '100mm',
        label_around_border: true,
      },
    };
  },
  mounted() {
    this.get();
  },
  methods: {
    get() {
      axios
        .get(route('settings.get.printer'))
        .then((response) => {
          this.printer = response.data;
          this.loading = false;
        })
        .catch(function () {
          this.$router.push('/admin/settings');
        });
    },
    save() {
      this.loading = true;
      axios
        .post(route('settings.set.printer'), this.printer)
        .then((response) => {
          this.loading = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
